import { create, StoreApi, UseBoundStore } from "zustand";
import { Capacitor } from "@capacitor/core";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { localPreferences, logEvent, logException } from "@um/ui";
import {
  AppVersionInfo,
  BuildInfo,
  BundleInfo,
  getAppVersion,
  getAppVersionFromCode,
  getLatestBundleId,
  UpdateBundle,
  withRefOrUndefined,
} from "@um/contracts";
import { getDocument } from "@um/ui";
import { getDoc } from "firebase/firestore";
import { getBuildInfo } from "@um/ui";

export const BUILTIN_BUNDLE_ID = "built-in";

/**
 * Delay before pruning superseded bundles so the delete I/O (and the
 * storage-footprint measurement walks at ~15s) stay off the startup path.
 */
const PRUNE_STALE_BUNDLES_DELAY_MS = 30_000;
let pruneScheduled = false;

/**
 * Delete every downloaded bundle except the ones in keepBundleIds (the
 * running bundle and a pending next bundle). Bundles were previously
 * retained forever — ~23 MB per OTA release accumulating on devices.
 * Fire-and-forget; once per session; a bundle deleted in error is simply
 * re-downloaded by the next update check.
 */
function schedulePruneStaleBundles(keepBundleIds: Set<string>) {
  if (pruneScheduled) return;
  pruneScheduled = true;

  setTimeout(async () => {
    try {
      const { bundleIds } = await LiveUpdate.getBundles();
      const stale = bundleIds.filter((id) => !keepBundleIds.has(id));
      for (const bundleId of stale) {
        console.log(`[auto-update] Pruning superseded bundle ${bundleId}`);
        await LiveUpdate.deleteBundle({ bundleId });
      }
      if (stale.length > 0) {
        logEvent("update_bundles_pruned", {
          deleted_count: stale.length,
          kept_count: bundleIds.length - stale.length,
        });
        // Drop pruned bundles from the metadata cache too (keep built-in).
        const trimmed = useAutoUpdateStoreInternal
          .getState()
          .bundleMetadata.filter(
            (b) =>
              b.bundleId === BUILTIN_BUNDLE_ID || keepBundleIds.has(b.bundleId)
          );
        useAutoUpdateStoreInternal.setState({ bundleMetadata: trimmed });
        localPreferences.setItem("bundleMetadata", JSON.stringify(trimmed));
      }
    } catch (error) {
      logException(error);
      console.error("[auto-update] Failed to prune stale bundles:", error);
    }
  }, PRUNE_STALE_BUNDLES_DELAY_MS);
}

export interface AutoUpdateStore {
  // State
  isInitialized: boolean | undefined;
  channel: string;
  updateError: string | null;
  isDownloading: boolean;
  isCheckingForUpdates: boolean;
  currentBuild: BuildInfo | null;
  appVersion: AppVersionInfo | null;
  currentBundle: BundleInfo | null;
  availableBundle: BundleInfo | null;
  nextBundle: BundleInfo | null;
  lastCheckTime: number;
  isAppInForeground: boolean;
  bundleMetadata: BundleInfo[];
  allowBundleUpdates: boolean;
  applyingUpdate: boolean;

  // Actions
  initialize: () => Promise<void>;
  setChannel: (channel: string) => void;
  checkForUpdates: () => Promise<BundleInfo | null>;
  downloadUpdate: (bundle: BundleInfo) => Promise<void>;
  applyUpdate: () => Promise<void>;
  deleteNextBundle: () => Promise<void>;
  revertToBuildIn: () => Promise<void>;
}

interface InternalAutoUpdateStore {
  /** Apply the build's channel as a default without clobbering an explicit user override. */
  setDefaultChannel: (channel: string) => void;
  setUpdateError: (error: string | null) => void;
  setIsDownloading: (isDownloading: boolean) => void;
  setCurrentBuild: (metadata: BuildInfo | null) => void;
  setCurrentBundle: (bundle: BundleInfo | null) => void;
  setNextBundle: (bundle: BundleInfo | null) => void;
  setIsInitialized: (fetched: boolean) => void;
  setLastCheckTime: (time: number) => void;
  addBundleMetadata: (bundle: BundleInfo) => void;
  setIsCheckingForUpdates: (isCheckingForUpdates: boolean) => void;
  setAvailableBundle: (bundle: BundleInfo | null) => void;
}

const useAutoUpdateStoreInternal = create<
  AutoUpdateStore & InternalAutoUpdateStore
>((set, get) => ({
  // Initial state
  isInitialized: undefined,
  channel: (() => {
    const saved = localPreferences.getItem("updateChannel");
    return saved || import.meta.env.VITE_CHANNEL || "production";
  })(),
  updateError: null,
  isDownloading: false,
  isCheckingForUpdates: false,
  currentBuild: null,
  appVersion: null,
  currentBundle: null,
  nextBundle: null,
  lastCheckTime: 0,
  isAppInForeground: true,
  versionCode: null,
  versionName: null,
  bundleMetadata: (() => {
    const saved = localPreferences.getItem("bundleMetadata");
    return saved ? (JSON.parse(saved) as BundleInfo[]) : [];
  })(),
  allowBundleUpdates: Capacitor.isNativePlatform(),
  applyingUpdate: false,
  availableBundle: null,

  addBundleMetadata: (bundle) => {
    const existingIndex = get().bundleMetadata.findIndex(
      (b) => b.bundleId === bundle.bundleId
    );
    const updatedMetadata =
      existingIndex >= 0
        ? get().bundleMetadata.map((b, i) => (i === existingIndex ? bundle : b))
        : [...get().bundleMetadata, bundle];
    set({ bundleMetadata: updatedMetadata });
    localPreferences.setItem("bundleMetadata", JSON.stringify(updatedMetadata));
  },

  // Simple setters
  setChannel: (channel) => {
    set({ channel });
    localPreferences.setItem("updateChannel", channel);
    // Remember this was an explicit choice so initialization defaults never clobber it.
    localPreferences.setItem("updateChannelSource", "user");
  },

  setDefaultChannel: (channel) => {
    if (localPreferences.getItem("updateChannelSource") === "user") {
      console.log(
        `[auto-update] Keeping user-selected channel '${get().channel}', ignoring build default '${channel}'`
      );
      return;
    }
    console.log(
      `[auto-update] Defaulting channel to '${channel}' from the current build`
    );
    set({ channel });
    localPreferences.setItem("updateChannel", channel);
  },
  setUpdateError: (updateError) => set({ updateError }),
  setIsDownloading: (isDownloading) => set({ isDownloading }),
  setCurrentBuild: (currentBuild) => set({ currentBuild }),
  setCurrentBundle: (currentBundle) => set({ currentBundle }),
  setNextBundle: (nextBundle) => set({ nextBundle }),
  setIsInitialized: (isInitialized) => set({ isInitialized }),
  setLastCheckTime: (lastCheckTime) => set({ lastCheckTime }),
  setIsCheckingForUpdates: (isCheckingForUpdates) =>
    set({ isCheckingForUpdates }),
  setAvailableBundle: (availableBundle) => set({ availableBundle }),

  deleteNextBundle: async () => {
    const { nextBundle, setNextBundle } = get();
    if (nextBundle) {
      await LiveUpdate.deleteBundle({ bundleId: nextBundle.bundleId });
      setNextBundle(null);
    }
  },

  revertToBuildIn: async () => {
    if (Capacitor.isNativePlatform()) {
      await LiveUpdate.reset();
      await LiveUpdate.reload();
    }
  },

  checkForUpdates: async () => {
    const {
      channel,
      setUpdateError,
      setIsCheckingForUpdates,
      setLastCheckTime,
      currentBundle,
      addBundleMetadata,
      setAvailableBundle,
      appVersion,
    } = get();

    if (!appVersion) {
      console.error(
        "[auto-update] App version is not set, must be initialized first. skipping update check"
      );
      return null;
    }

    setUpdateError(null);
    setIsCheckingForUpdates(true);

    console.log(`[auto-update] Checking for updates...`);

    setLastCheckTime(Date.now());

    const shouldUpdate = (newBundle: BundleInfo) => {
      // TODO: consider also validating that the bundle is compatable with the build
      if (!currentBundle?.version) {
        console.log(
          `[auto-update] Skipping update because current bundle has no version info, likley a developer build`
        );
        return false;
      }
      if (!newBundle.version) {
        console.log(
          `[auto-update] Skipping update because new bundle has no version info`
        );
        return false;
      }

      // TODO: consider only taking newer versions
      // TODO: consider the channel that the bundle is from
      if (newBundle.version !== currentBundle.version) {
        console.log(
          `[auto-update] New bundle version ${newBundle.version} is different from current bundle version ${currentBundle?.version}`
        );
        return true;
      }

      // the versions are the same, so we need to check the bundle id
      if (currentBundle.bundleId === BUILTIN_BUNDLE_ID) {
        console.log(
          `[auto-update] Current bundle is the builtin bundle, and the new bundle has the same version as the builtin bundle (${currentBundle.version}) so we are ignoring it.`
        );
        return false;
      }

      if (newBundle.bundleId !== currentBundle.bundleId) {
        console.warn(
          `[auto-update] The bundles have the same version number ${newBundle.version} but different bundle ids ${newBundle.bundleId} and ${currentBundle.bundleId}.  updating to the new bundle.`
        );
        return true;
      }

      console.log(
        `[auto-update]  ${currentBundle.bundleId} - ${currentBundle.version} is already applied, no need to update.`
      );
      return false;
    };

    try {
      if (Capacitor.isNativePlatform()) {
        try {
          const startTime = performance.now();
          //const resultLatest = await LiveUpdate.fetchLatestBundle({ channel });
          const latestBundleRef = getDocument<UpdateBundle>(
            `UpdateBundle/${getLatestBundleId(channel, appVersion)}`
          );
          const latestBundle = (await getDoc(latestBundleRef)).data();
          const endTime = performance.now();
          console.log(
            `[auto-update] fetchLatestBundle took ${endTime - startTime}ms`
          );

          const newBundle: BundleInfo | null = latestBundle
            ? {
                bundleId: latestBundle.bundleId,
                version: latestBundle.version,
                buildNumber: latestBundle.buildNumber,
                commitSha: latestBundle.commitSha,
                runId: latestBundle.runId,
                workflow: latestBundle.workflow,
                forceUpdate: latestBundle.forceUpdate,
                downloadUrl: latestBundle.downloadUrl,
                buildTimestamp: latestBundle.buildTimestamp,
                channel,
              }
            : null;

          // Only update if the bundle is different than the built in one.
          if (newBundle && shouldUpdate(newBundle)) {
            addBundleMetadata(newBundle);
            setAvailableBundle(newBundle);
            return newBundle;
          }
        } catch (error: any) {
          logException(error);
          console.error("[auto-update] Auto-update failed:", error);
          setUpdateError(error?.message || "Failed to refresh update");

          // Show error toast for debugging
          if (
            error?.message?.includes("No bundle found") ||
            error?.message?.includes("No update available")
          ) {
            console.log(
              "[auto-update] No bundle available for channel:",
              channel
            );
          } else {
            setUpdateError("Update check failed");

            // If this is a critical native client error, suggest a manual restart
            if (
              error?.message?.includes("Native Client") ||
              error?.message?.includes("not available")
            ) {
              console.error(
                "🚨 Critical error detected - native client unavailable"
              );
              setUpdateError(
                "Please restart the app to ensure proper functionality"
              );
            }
          }
        }
      } else {
        // deployed as website so just check to see if a new build was published
        const buildInfo = await getBuildInfo();
        const newBundle = buildInfo
          ? { bundleId: BUILTIN_BUNDLE_ID, ...buildInfo }
          : null;
        if (newBundle && shouldUpdate(newBundle)) {
          setAvailableBundle(newBundle);
          return newBundle;
        }
      }
    } finally {
      setIsCheckingForUpdates(false);
    }
    return null;
  },

  downloadUpdate: async (newBundle: BundleInfo) => {
    const {
      channel,
      setUpdateError,
      setIsDownloading,
      setNextBundle,
      setLastCheckTime,
      setAvailableBundle,
      isDownloading,
    } = get();

    if (isDownloading) {
      console.log(
        `[auto-update] Skipping update because we are already downloading`
      );
      return;
    }

    setUpdateError(null);
    setIsDownloading(true);

    console.log(`[auto-update] Checking for updates...`);

    setLastCheckTime(Date.now());

    try {
      if (Capacitor.isNativePlatform()) {
        try {
          console.log(
            `[auto-update] next update available: ${newBundle.version} ${newBundle.bundleId}`
          );

          const bundleResults = await LiveUpdate.getBundles();
          console.log(
            `[auto-update] downloaded bundles: ${JSON.stringify(bundleResults)}`
          );
          if (!bundleResults.bundleIds.includes(newBundle.bundleId)) {
            console.log(
              `[auto-update] downloading bundle ${newBundle.bundleId}`
            );

            if (!newBundle.downloadUrl) {
              throw new Error("No download url found for bundle");
            }

            // Download the new bundle
            await LiveUpdate.downloadBundle({
              bundleId: newBundle.bundleId,
              url: newBundle.downloadUrl,
            });
          }

          const { bundleId: nextBundleId } = await LiveUpdate.getNextBundle();
          if (nextBundleId !== newBundle.bundleId) {
            console.log(
              `[auto-update] setting next bundle to ${newBundle.bundleId}. next bundle was ${nextBundleId}`
            );
            await LiveUpdate.setNextBundle({ bundleId: newBundle.bundleId });
            setAvailableBundle(null);
            setNextBundle(newBundle);
          }
          console.log("[auto-update] Update downloaded and ready to apply");
        } catch (error: any) {
          logException(error);
          console.error("[auto-update] Auto-update failed:", error);
          setUpdateError(error?.message || "Failed to refresh update");

          // Show error toast for debugging
          if (
            error?.message?.includes("No bundle found") ||
            error?.message?.includes("No update available")
          ) {
            console.log(
              "[auto-update] No bundle available for channel:",
              channel
            );
          } else {
            setUpdateError("Update check failed");

            // If this is a critical native client error, suggest a manual restart
            if (
              error?.message?.includes("Native Client") ||
              error?.message?.includes("not available")
            ) {
              console.error(
                "🚨 Critical error detected - native client unavailable"
              );
              setUpdateError(
                "Please restart the app to ensure proper functionality"
              );
            }
          }
        }
      } else {
        setAvailableBundle(null);
        setNextBundle(newBundle);
      }
    } finally {
      setIsDownloading(false);
    }
  },

  applyUpdate: async () => {
    const { nextBundle, setUpdateError } = get();

    setUpdateError(null);
    if (!nextBundle) {
      setUpdateError("No update to apply");
      return;
    }

    // This does not need to be unset because the app will be reloaded
    set({ applyingUpdate: true });

    if (Capacitor.isNativePlatform()) {
      try {
        console.log("[auto-update] Applying downloaded update...");

        const currentUrl = window.location.href;
        localPreferences.setItem("pendingUpdateUrl", currentUrl);

        await LiveUpdate.reload();
      } catch (error: any) {
        console.error("[auto-update] Failed to apply update:", error);
        setUpdateError(error?.message || "Failed to refresh update");
        // Fallback to regular page reload
        // TODO: Is this the right thing to do here?
        window.location.reload();
      }
    } else {
      // deployed as website
      console.log("[auto-update] Reloading page for website update...");
      window.location.reload();
    }
  },

  initialize: async () => {
    const {
      isInitialized,
      setNextBundle,
      setCurrentBundle,
      setIsInitialized,
      setCurrentBuild,
      bundleMetadata,
      setDefaultChannel,
      addBundleMetadata,
    } = get();

    if (isInitialized !== undefined) {
      return;
    }
    setIsInitialized(false);

    const buildInfo = await getBuildInfo();

    if (Capacitor.isNativePlatform()) {
      const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle();
      const { bundleId: nextBundleId } = await LiveUpdate.getNextBundle();
      const { versionCode } = await LiveUpdate.getVersionCode();
      const { versionName } = await LiveUpdate.getVersionName();
      const appVersion = getAppVersionFromCode(
        Number(versionCode),
        versionName
      );

      console.log(`[auto-update] current bundle: ${currentBundleId} `);
      console.log(`[auto-update] next bundle: ${nextBundleId}`);

      // trusting the cache creates a consistent behavior when debugging with a remote dev server
      const cachedBundle = currentBundleId
        ? bundleMetadata.find((b) => b.bundleId === currentBundleId)
        : undefined;
      const currentBundle: BundleInfo = cachedBundle || {
        bundleId: currentBundleId || BUILTIN_BUNDLE_ID,
        ...buildInfo,
      };
      set({
        currentBundle,
        appVersion,
      });

      if (currentBundleId) {
        // we have an active bundle get the build in bundle from the metadata
        const buildInBundle = bundleMetadata.find(
          (b) => b.bundleId === BUILTIN_BUNDLE_ID
        );
        if (buildInBundle) {
          setCurrentBuild(buildInBundle);
        } else {
          console.warn(
            `[auto-update] Unexpected state.  We have a current bundle ${currentBundle.bundleId} - ${currentBundle.version} but no build in bundle in the metadata cache.`
          );
          setCurrentBuild(currentBundle);
        }
      } else {
        // this is the built-in bundle and we need to remember it when we are not using the native build
        console.log(
          `[auto-update] Starting on the built-in bundle (channel '${get().channel}')`
        );
        setCurrentBuild(currentBundle);
        addBundleMetadata(currentBundle);
        if (currentBundle?.channel) {
          // default the channel to the originating build's, unless the user picked one
          setDefaultChannel(currentBundle.channel);
        }
      }

      if (nextBundleId === currentBundle.bundleId) {
        console.log(
          `[auto-update] Update is completed with bundle ${currentBundle.bundleId} so clearing next bundle.`
        );
        LiveUpdate.setNextBundle({ bundleId: null });
        setNextBundle(null);
      } else if (nextBundleId) {
        const nextBundle = bundleMetadata.find(
          (b) => b.bundleId === nextBundleId
        );
        if (nextBundle) {
          setNextBundle(nextBundle);
        } else {
          // this is an unexpected state where we dont have the bundle metadata so we wont trust the downloaded bundle
          console.warn(
            `[auto-update] Unexpected state.  We have a next bundle ${nextBundleId} but no bundle in the metadata cache. clearing the next bundle.`
          );
          LiveUpdate.setNextBundle({ bundleId: null });
        }
      }

      // Only the running bundle and a surviving next bundle are ever needed
      // again — schedule deletion of everything else.
      const keptNextBundleId =
        nextBundleId &&
        nextBundleId !== currentBundle.bundleId &&
        bundleMetadata.some((b) => b.bundleId === nextBundleId)
          ? nextBundleId
          : null;
      schedulePruneStaleBundles(
        new Set(
          [currentBundleId, keptNextBundleId].filter((id): id is string => !!id)
        )
      );
    } else {
      // for web the current bundle is always the builtin bundle
      setCurrentBuild(buildInfo);
      set({ appVersion: buildInfo ? getAppVersion(buildInfo) : null });
      setCurrentBundle({ bundleId: BUILTIN_BUNDLE_ID, ...buildInfo });
    }

    setIsInitialized(true);
  },
}));

// export just the public facing api
export const useAutoUpdateStore = useAutoUpdateStoreInternal as UseBoundStore<
  StoreApi<AutoUpdateStore>
>;
